import type { LanguageCode } from './facility';
import { languageCodeValues } from './facility';

// SINGLE SOURCE OF TRUTH for language display metadata across the whole app.
//
// The set of codes lives next door in `facility.ts` (`languageCodeValues`, the
// Zod enum backend translation validates against). This file owns everything
// ELSE about a language — its English name, its native name (endonym), the
// country whose flag represents it, and the picker priority order — so no
// domain has to keep its own hand-maintained copy. Parity with the legacy V2
// `LanguageCodeObjects` (travel_planner_back src/common/enum/language-code.ts).
//
// Consumers must derive from here (`LANGUAGE_META`, `languageName`,
// `languageEndonym`, `languageFlag`, `languageOptions`, `compareLanguages`)
// rather than declaring their own `{ code, label }` arrays.

export type LanguageMeta = {
  // English exonym, e.g. 'Japanese'. This is the label V2 showed everywhere.
  name: string;
  // Native name (endonym), e.g. '日本語'. Used where the picker prefers the
  // language's own name.
  endonym: string;
  // ISO 3166-1 alpha-2 country whose flag stands in for the language. Languages
  // without a 1:1 country (en, ar) use the conventional pick (en→GB, ar→SA).
  country: string;
};

// Keyed by every `languageCodeValues` code — the `Record<LanguageCode, …>`
// type makes a missing entry a compile error, so this can never drift from the
// enum.
export const LANGUAGE_META: Record<LanguageCode, LanguageMeta> = {
  en: { name: 'English', endonym: 'English', country: 'GB' },
  es: { name: 'Spanish', endonym: 'Español', country: 'ES' },
  pt: { name: 'Portuguese', endonym: 'Português', country: 'PT' },
  fr: { name: 'French', endonym: 'Français', country: 'FR' },
  de: { name: 'German', endonym: 'Deutsch', country: 'DE' },
  it: { name: 'Italian', endonym: 'Italiano', country: 'IT' },
  ru: { name: 'Russian', endonym: 'Русский', country: 'RU' },
  zh: { name: 'Chinese (Simplified)', endonym: '中文（简体）', country: 'CN' },
  ja: { name: 'Japanese', endonym: '日本語', country: 'JP' },
  ko: { name: 'Korean', endonym: '한국어', country: 'KR' },
  ar: { name: 'Arabic', endonym: 'العربية', country: 'SA' },
  hi: { name: 'Hindi', endonym: 'हिन्दी', country: 'IN' },
  nl: { name: 'Dutch', endonym: 'Nederlands', country: 'NL' },
  sv: { name: 'Swedish', endonym: 'Svenska', country: 'SE' },
  pl: { name: 'Polish', endonym: 'Polski', country: 'PL' },
  tr: { name: 'Turkish', endonym: 'Türkçe', country: 'TR' },
  vi: { name: 'Vietnamese', endonym: 'Tiếng Việt', country: 'VN' },
  th: { name: 'Thai', endonym: 'ไทย', country: 'TH' },
  id: { name: 'Indonesian', endonym: 'Indonesia', country: 'ID' },
  el: { name: 'Greek', endonym: 'Ελληνικά', country: 'GR' },
  al: { name: 'Albanian', endonym: 'Shqip', country: 'AL' },
  da: { name: 'Danish', endonym: 'Dansk', country: 'DK' },
  no: { name: 'Norwegian', endonym: 'Norsk', country: 'NO' },
};

// Primary markets pinned to the top of language pickers (in this order),
// matching legacy ordering (travel_planner_front `LANGUAGE_PRIORITY`);
// everything else follows in display order. English leads so an unset language
// opens on English rather than Portuguese.
export const PRIORITY_LANGUAGES: readonly LanguageCode[] = [
  'en',
  'pt',
  'es',
  'fr',
];

const isLanguageCode = (code: string): code is LanguageCode =>
  (languageCodeValues as readonly string[]).includes(code);

// Normalise arbitrary input (upper/lower case, surrounding space) to a known
// code, or null when it isn't one of ours.
export const toLanguageCode = (
  code: string | null | undefined
): LanguageCode | null => {
  if (!code) return null;
  const lower = code.trim().toLowerCase();
  return isLanguageCode(lower) ? lower : null;
};

const folded = (text: string): string =>
  text.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase();

const BY_NAME = new Map<string, LanguageCode>(
  (languageCodeValues as readonly LanguageCode[]).flatMap((code) => [
    [folded(LANGUAGE_META[code].name), code],
    [folded(LANGUAGE_META[code].endonym), code],
  ])
);

// A language as free text names it — a code, its English name or its endonym,
// in any case and with or without accents ('pt', 'Portuguese', 'portugues') —
// as one of our codes, or null when it names none of them.
export const languageCodeFrom = (
  text: string | null | undefined
): LanguageCode | null => {
  if (!text) return null;
  return toLanguageCode(text) ?? BY_NAME.get(folded(text)) ?? null;
};

// English exonym for a code. Falls back to the upper-cased input so an unknown
// code (or an already-English name) still renders something.
export const languageName = (code: string | null | undefined): string => {
  const known = toLanguageCode(code);
  if (known) return LANGUAGE_META[known].name;
  return code ? code.toUpperCase() : '';
};

// Native name (endonym) for a code, same fallback as `languageName`.
export const languageEndonym = (code: string | null | undefined): string => {
  const known = toLanguageCode(code);
  if (known) return LANGUAGE_META[known].endonym;
  return code ? code.toUpperCase() : '';
};

// Flag emoji built from the language's country's regional-indicator
// codepoints. Empty string for an unknown code.
export const languageFlag = (code: string | null | undefined): string => {
  const known = toLanguageCode(code);
  if (!known) return '';
  const country = LANGUAGE_META[known].country;
  return String.fromCodePoint(
    ...[...country].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65)
  );
};

export type LanguageOption = { value: LanguageCode; label: string };

// Build `{ value, label }` picker options for every language. `label` defaults
// to the English exonym; pass `'endonym'` for native names. `sort: 'priority'`
// puts the primary markets first (then display order); `'alpha'` sorts purely
// by label; omit to keep the canonical `LANGUAGE_META` order.
export const languageOptions = (opts?: {
  label?: 'name' | 'endonym';
  sort?: 'priority' | 'alpha';
}): LanguageOption[] => {
  const labelKey = opts?.label ?? 'name';
  const options = (languageCodeValues as readonly LanguageCode[]).map(
    (value) => ({ value, label: LANGUAGE_META[value][labelKey] })
  );
  if (opts?.sort === 'alpha') {
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }
  if (opts?.sort === 'priority') {
    return options.sort((a, b) => compareLanguages(a.value, b.value));
  }
  return options;
};

// Sort comparator: priority markets first (in `PRIORITY_LANGUAGES` order),
// then everything else alphabetically by English name.
export const compareLanguages = (a: LanguageCode, b: LanguageCode): number => {
  const ai = PRIORITY_LANGUAGES.indexOf(a);
  const bi = PRIORITY_LANGUAGES.indexOf(b);
  if (ai !== -1 || bi !== -1) {
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  }
  return LANGUAGE_META[a].name.localeCompare(LANGUAGE_META[b].name);
};
